import type { Customer } from './types'

// "Download your customers" on Work (Liam 2026-09-26: yes to "Your customers
// stay yours if you stop"). The whole list as a spreadsheet the owner can keep,
// open in Excel or Numbers, or bring back in through Import: the first columns
// carry the headings Import reads (Name, Address, Postcode, Phone, Email, Area, Price,
// How often, Access notes, Notes), the rest are everything else Roundly holds
// about the customer, and at the end what they owe now and when they were last cleaned, are next booked and are next
// due (27 Oct 2026, with "Download your visits and payments" below).

export const esc = (v: string): string => {
  // A cell starting = + - @ is run as a formula by Excel; a phone number like
  // +44... is kept as text by the leading apostrophe instead.
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

const pounds = (pence: number | null): string => (pence === null || pence === undefined ? '' : (pence / 100).toFixed(2))

// Written the way Import reads it back: "4 weekly", "10 days".
export function cycleWords(c: Pick<Customer, 'cycle_days' | 'cycle_text'>): string {
  const d = c.cycle_days
  if (d && d > 0) return d % 7 === 0 ? `${d / 7} weekly` : `${d} days`
  return c.cycle_text ?? ''
}

const COLUMNS: [string, (c: Customer) => string][] = [
  ['Name', (c) => c.name],
  ['Address', (c) => c.address ?? ''],
  ['Postcode', (c) => c.postcode ?? ''],
  ['Phone', (c) => c.phone ?? ''],
  ['Email', (c) => c.email ?? ''],
  ['Area', (c) => c.area ?? ''],
  ['Price', (c) => pounds(c.reg_price_pence)],
  ['How often', (c) => cycleWords(c)],
  ['Access notes', (c) => c.job_notes ?? ''],
  ['Notes', (c) => c.notes ?? ''],
  ['Round list', c => c.customer_list ?? ''],
  ['Round list date', c => c.customer_list_on ?? ''],
  ['Round list reason', c => c.customer_list_reason ?? ''],
  ['Recanvass review date', c => c.customer_list_review_on ?? ''],
  ['Messages shelf', c => c.messages_shelf ?? ''],
  ['Shelved on', c => c.messages_shelved_on ?? ''],
  ['Visit reason', c => c.messages_shelf_reason ?? ''],
  ['Visit review date', c => c.messages_shelf_review_on ?? ''],
  ['Status', (c) => c.status === 'paused' && c.message_reply === 'stopped' ? 'stopped' : c.status],
  ['Inside price', (c) => pounds(c.inside_price_pence)],
  ['First clean price', (c) => pounds(c.first_clean_price_pence)],
  ['Time taken', (c) => c.time_taken ?? ''],
  ['Parking', (c) => c.parking_restrictions ?? ''],
  ['Preferred times', (c) => c.preferred_times ?? ''],
  ['Call notes', (c) => c.call_notes ?? ''],
  ['Map link', (c) => c.maps_link ?? ''],
  ['What they said', (c) => c.said_note ?? ''],
  ['Reply note date', (c) => c.said_on ?? ''],
  ['Contact notes', (c) => c.contact_note ?? ''],
  ['Follow up on', (c) => c.follow_up_on ?? ''],
  ['Reply outcome', (c) => c.message_reply ?? ''],
  ['Reply date', (c) => c.message_reply_on ?? ''],
  ['Offered dates', (c) => c.message_reply_dates ?? ''],
  ['Reply job ID (blank means main job)', (c) => c.message_reply_job_id == null ? '' : String(c.message_reply_job_id)],
  ['Job conversations (JSON)', (c) => c.message_replies ?? ''],
  // Their own way, when they have one (0036); blank = your usual.
  ['Messages by', (c) => (c.message_by === 'none' ? "Don't message" : c.message_by === 'text' ? 'Text' : c.message_by === 'email' ? 'Email' : c.message_by === 'whatsapp' ? 'WhatsApp' : '')],
]

// What the screen knows about each customer beyond their own row, for the last columns (grid.ts passes them):
// owedPence, the same figure Owed adds up for them (visits marked not paid, less anything already on them, and
// money brought from Squeegee still owing; "Payment not recorded" is not owed, as on Owed); dueOn, the next due day
// as Messages and the Diary work it out (planning.ts dueDate); symbol, the round's money sign for the heading.
export interface CustomerExtras { owedPence: (c: Customer) => number; dueOn: (c: Customer) => string | null; symbol: string }

// The columns after the first ten never match a heading Import reads (scripts/customer-export.test.mjs).
const extraColumns = (x: CustomerExtras): [string, (c: Customer) => string][] => [
  [`Owed now (${x.symbol})`, (c) => pounds(x.owedPence(c))],
  ['Last cleaned', (c) => c.last_clean ?? ''],
  ['Next booked', (c) => c.next_booked ?? ''],
  ['Next due', (c) => x.dueOn(c) ?? ''],
]

const BOM = '﻿'

// words: the round's own words for a heading (the screen passes postcodeWords: "ZIP code" in the US; Step 2 round 3).
export function customersCsv(customers: Customer[], words: (heading: string) => string = (h) => h, extras?: CustomerExtras): string {
  const columns = extras ? [...COLUMNS, ...extraColumns(extras)] : COLUMNS
  const rows = [columns.map(([h]) => words(h)).join(',')]
  const sorted = [...customers].sort((a, b) => a.name.localeCompare(b.name, 'en-GB'))
  for (const c of sorted) rows.push(columns.map(([, f]) => esc(f(c))).join(','))
  // A byte-order mark so Excel reads £ and accents as they are.
  return BOM + rows.join('\r\n') + '\r\n'
}

// Owed's figure for each customer, from GET /api/owed as the screen reads it: the visits marked not paid (what is
// still to pay on each when money was brought from Squeegee, else the price) and the brought accounts still owing.
export interface OwedRead {
  cleans: { customer_id: number; amount_pence: number | null; to_pay_pence?: number | null }[]
  brought?: { accounts: { customer_id: number; left_pence: number }[] }
}
export function owedByCustomer(owed: OwedRead): Map<number, number> {
  const out = new Map<number, number>()
  const add = (id: number, pence: number): void => { out.set(id, (out.get(id) ?? 0) + pence) }
  for (const c of owed.cleans) add(c.customer_id, c.to_pay_pence ?? c.amount_pence ?? 0)
  for (const a of owed.brought?.accounts ?? []) if (a.left_pence > 0) add(a.customer_id, a.left_pence)
  return out
}

// "Download your visits and payments" (27 Oct 2026): one row per visit, oldest first, from GET /api/visits-download.
export interface VisitRow {
  date: string; state: string; amount_pence: number | null; payment_status: string; payment_method: string | null
  chase_count: number; customer_name: string; customer_address: string | null; customer_postcode: string | null; paid_on: string | null
}

const STATE_WORD: Record<string, string> = { booked: 'Booked', done: 'Done', cancelled: 'Cancelled' }
// Paid or not only means something once the visit is done.
const paidWord = (v: VisitRow): string => v.state !== 'done' ? '' : v.payment_status === 'paid' ? 'Paid' : v.payment_status === 'unpaid' ? 'Not paid' : 'Not recorded'
const howPaid = (v: VisitRow): string => v.state === 'done' && v.payment_status === 'paid' ? (v.payment_method === 'cash' ? 'Cash' : v.payment_method === 'bank' ? 'Bank' : '') : ''

export function visitsCsv(visits: VisitRow[], symbol: string, words: (heading: string) => string = (h) => h): string {
  const columns: [string, (v: VisitRow) => string][] = [
    ['Customer', (v) => v.customer_name],
    ['Address', (v) => v.customer_address ?? ''],
    ['Postcode', (v) => v.customer_postcode ?? ''],
    ['Date', (v) => v.date],
    ['Status', (v) => STATE_WORD[v.state] ?? v.state],
    [`Price (${symbol})`, (v) => pounds(v.amount_pence)],
    ['Paid', paidWord],
    ['How paid', howPaid],
    ['Paid on', (v) => v.paid_on ?? ''],
    ['Times chased', (v) => (v.chase_count ? String(v.chase_count) : '0')],
  ]
  const rows = [columns.map(([h]) => words(h)).join(',')]
  // Oldest first, as the server sends them (the sort is kept here too so the file never depends on it).
  const sorted = [...visits].sort((a, b) => a.date.localeCompare(b.date))
  for (const v of sorted) rows.push(columns.map(([, f]) => esc(f(v))).join(','))
  return BOM + rows.join('\r\n') + '\r\n'
}
